import { useState, useEffect, useRef } from "react";
import { Database, DownloadSimple, ShieldCheck, CloudArrowDown, Archive, Briefcase, EnvelopeSimple, UploadSimple, ArrowCounterClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const CSVS = [
  { k: "branches", label: "Bank Branches" },
  { k: "clients", label: "Clients" },
  { k: "jobs", label: "Valuation Jobs" },
  { k: "invoices", label: "Invoices" },
  { k: "followups", label: "Follow-ups" },
];

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function DataBackup() {
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restoreRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [last, setLast] = useState(null);
  const [history, setHistory] = useState([]);
  const [freq, setFreq] = useState("weekly");

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(Array.isArray(data) ? data : (data?.jobs || []))).catch(() => {});
    api.get("/backup/history").then(({ data }) => { const list = data || []; setHistory(list); setLast(list[0] || null); }).catch(() => {});
    api.get("/backup/settings").then(({ data }) => setFreq(data?.frequency || "weekly")).catch(() => {});
  }, []);

  const saveFreq = async (f) => {
    const prev = freq;
    setFreq(f);
    try { await api.put("/backup/settings", { frequency: f }); toast.success(`Automatic backups set to ${f}`); }
    catch (err) { setFreq(prev); toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const freqLabel = freq === "daily" ? "every day" : freq === "monthly" ? "on the 1st of every month" : "every Monday";

  const jsonBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/backup/all");
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `aakriti-hub-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("JSON backup downloaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onRestore = async (e) => {
    const f = e.target?.files?.[0];
    if (!f) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restore complete — ${data.total_restored} record(s) recovered, ${data.total_skipped} already present`);
      api.get("/jobs").then(({ data }) => setJobs(Array.isArray(data) ? data : (data?.jobs || []))).catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRestoring(false); if (e.target) e.target.value = ""; }
  };

  const filtered = jobs.filter((j) => {
    const s = `${j.reference || ""} ${j.applicant_name || ""} ${j.bank_name || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  }).slice(0, 60);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="data-backup-page">
      <PageHeader overline="My Account" title="Data & Backup" subtitle="Your data is yours — take a full copy whenever you want" />

      <Card className="p-6 border-border">
        <SectionTitle icon={Archive}>Complete backup (with files)</SectionTitle>
        <p className="text-sm text-muted-foreground">One ZIP containing every record plus all uploaded documents &amp; site photos, organized job-by-job. This is the copy to keep on your own drive.</p>
        <Button asChild className="rounded-full bg-primary hover:bg-[#113A64] mt-4" data-testid="full-zip-backup-btn">
          <a href={`${API}/backup/full.zip`}><CloudArrowDown size={16} className="mr-1" /> Download Complete Backup (ZIP)</a>
        </Button>
        {last && (
          <p className="text-xs text-muted-foreground mt-3" data-testid="last-auto-backup">
            Last automatic backup: {new Date(last.created_at).toLocaleString()} · {fmtSize(last.size)} · emailed to you
          </p>
        )}
        <div className="mt-4">
          <div className="text-xs font-semibold text-primary mb-1.5">Automatic backup frequency</div>
          <div className="inline-flex rounded-full border border-border p-0.5" data-testid="backup-frequency-group">
            {["daily", "weekly", "monthly"].map((f) => (
              <button key={f} type="button" onClick={() => saveFreq(f)} data-testid={`backup-frequency-${f}`}
                className={`px-3.5 py-1 text-xs rounded-full capitalize transition-colors ${freq === f ? "bg-primary text-white" : "text-muted-foreground hover:text-primary"}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
          <EnvelopeSimple size={16} className="text-emerald-700 mt-0.5" />
          <p className="text-xs text-emerald-800">A fresh complete backup is generated <b>{freqLabel}</b> and emailed to the owner with a secure one-click download link — so you always have an off-site copy.</p>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle icon={ClockCounterClockwise}>Backup history</SectionTitle>
        <p className="text-sm text-muted-foreground mb-3">Your recent automatic and manual complete backups. Re-download any one as a ZIP.</p>
        <div className="divide-y divide-border rounded-lg border border-border" data-testid="backup-history-list">
          {history.length === 0 && <p className="text-sm text-muted-foreground p-3" data-testid="backup-history-empty">No backups yet — the first automatic backup runs on the next Monday.</p>}
          {history.slice(0, 12).map((b) => (
            <div key={b.id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`backup-history-${b.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-primary truncate">
                  {new Date(b.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  <span className={`ml-2 align-middle text-[10px] px-1.5 py-0.5 rounded-full ${b.source === "manual" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{b.source === "manual" ? "Manual" : "Auto"}</span>
                </div>
                <div className="text-xs text-muted-foreground truncate">{fmtSize(b.size)}{b.job_count != null ? ` · ${b.job_count} job(s)` : ""}{b.emailed_to?.length ? " · emailed" : ""}</div>
              </div>
              <Button asChild size="sm" variant="outline" className="rounded-full shrink-0" data-testid={`backup-history-download-${b.id}`}>
                <a href={`${API}/backup/history/${b.id}.zip`}><DownloadSimple size={14} className="mr-1" /> ZIP</a>
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle icon={Briefcase}>Backup a single job</SectionTitle>
        <p className="text-sm text-muted-foreground mb-3">Download any one job's record plus all its documents &amp; photos as its own ZIP.</p>
        <Input placeholder="Search by reference, applicant or bank…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3" data-testid="job-backup-search" />
        <div className="max-h-72 overflow-auto divide-y divide-border rounded-lg border border-border" data-testid="job-backup-list">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground p-3">No jobs found.</p>}
          {filtered.map((j) => (
            <div key={j.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-primary truncate">{j.reference || "—"} · {j.applicant_name || "Unnamed"}</div>
                <div className="text-xs text-muted-foreground truncate">{j.bank_name || ""}{j.property_address ? ` · ${j.property_address}` : ""}</div>
              </div>
              <Button asChild size="sm" variant="outline" className="rounded-full shrink-0" data-testid={`job-backup-${j.id}`}>
                <a href={`${API}/backup/jobs/${j.id}.zip`}><DownloadSimple size={14} className="mr-1" /> ZIP</a>
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle icon={ArrowCounterClockwise}>Restore from a backup</SectionTitle>
        <p className="text-sm text-muted-foreground">Recover deleted records by re-importing a Complete Backup ZIP. Records that still exist are left untouched — only missing ones are added back, so nothing current is overwritten.</p>
        <input ref={restoreRef} type="file" accept=".zip,application/zip" className="hidden" onChange={onRestore} data-testid="restore-file-input" />
        <Button onClick={() => restoreRef.current?.click()} disabled={restoring} variant="outline" className="rounded-full mt-4 border-primary text-primary hover:bg-primary/5 hover:text-primary" data-testid="restore-backup-btn">
          <UploadSimple size={16} className="mr-1" /> {restoring ? "Restoring…" : "Upload Backup ZIP to Restore"}
        </Button>
        <p className="text-xs text-muted-foreground mt-3">Tip: use the most recent Complete Backup (ZIP) you downloaded. Uploaded files &amp; photos are not re-imported — only database records.</p>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle icon={Database}>Records only (JSON)</SectionTitle>
        <p className="text-sm text-muted-foreground">One JSON file with every branch, client, job, invoice, follow-up, template and setting. No uploaded files. Passwords are never included.</p>
        <Button onClick={jsonBackup} disabled={busy} variant="outline" className="rounded-full mt-4" data-testid="full-backup-btn">
          <DownloadSimple size={16} className="mr-1" /> {busy ? "Preparing…" : "Download Records (JSON)"}
        </Button>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle icon={DownloadSimple}>Spreadsheet exports</SectionTitle>
        <p className="text-sm text-muted-foreground mb-4">CSV files that open directly in Excel.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {CSVS.map((c) => (
            <Button key={c.k} asChild variant="outline" className="rounded-full justify-start">
              <a href={`${API}/export/${c.k}`} data-testid={`export-${c.k}-btn`}><DownloadSimple size={15} className="mr-2" /> {c.label}</a>
            </Button>
          ))}
          <Button asChild variant="outline" className="rounded-full justify-start">
            <a href={`${API}/export/activity/log`} data-testid="export-activity-btn"><DownloadSimple size={15} className="mr-2" /> Activity Log</a>
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-border bg-muted/40">
        <SectionTitle icon={ShieldCheck}>How your data is kept safe</SectionTitle>
        <ul className="text-sm text-muted-foreground space-y-2 list-disc pl-5">
          <li>A complete backup is generated automatically {freqLabel} and emailed to the owner with a secure download link.</li>
          <li>Records live in a managed database with automatic snapshots taken by the hosting platform.</li>
          <li>Documents and site photos are stored in managed object storage, not in the database.</li>
          <li>Passwords are stored as one-way hashes — nobody, including us, can read them.</li>
          <li>Only the Owner role can export data or take a backup.</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-4">Tip: keep each weekly ZIP on your own drive or cloud for a fully independent copy.</p>
      </Card>
    </div>
  );
}
